import React from 'react';
import _ from 'underscore';

import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';

import { RouterError } from '../../../../bootstrap/routerHelpers/RouterError';
import { SessionManagementService } from '../../../../sessionManagement/SessionManagementService';
import { Sites } from '../../../../siteManagement/services/resources/Sites';
import { GeneralSiteSettingsCoordinator } from '../../../general-settings/components/site-settings/GeneralSiteSettingsCoordinator';
import { IEditableServerSettings } from '../../../ServerApi/types/IEditableServerSettings';
import { ServerSettings } from '../../../services/ServerSettings';
import {
  ISiteAuthenticationSettings,
  SiteAuthenticationSettingsService,
} from '../../../siteAuthSettings/services/SiteAuthenticationSettingsService';
import { ISiteSettingsForServerAdmin } from '../../../types/ISite';
import { ISiteSettingsForSiteAdmin } from '../../../types/ISiteSettingsForSiteAdmin';

export interface GeneralSiteSettingsSubspaceOwnProps {
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
  readonly server: SessionManagementService;
  readonly isSessionMultiSite: boolean;
  readonly isSessionServerAdmin: boolean;
}

export interface GeneralSiteSettingsSubspaceServicesProps {
  readonly serverSettingsService: ServerSettings;
}

export type GeneralSiteSettingsSubspaceProps = GeneralSiteSettingsSubspaceOwnProps & GeneralSiteSettingsSubspaceServicesProps;

export interface GeneralSiteSettingsSubspaceState {
  readonly hasDataToRender: boolean;
  readonly hasError: boolean;
  readonly serverSettings: IEditableServerSettings;
  readonly siteSettings: ISiteSettingsForSiteAdmin;
  readonly siteSettingsForServerAdmin: ISiteSettingsForServerAdmin;
  readonly siteAuthenticationSettings: ISiteAuthenticationSettings;
}

export class GeneralSiteSettingsSubspace extends React.PureComponent<GeneralSiteSettingsSubspaceProps, GeneralSiteSettingsSubspaceState> {

  private unmounted: boolean = false;

  private initialState: GeneralSiteSettingsSubspaceState = {
    hasDataToRender: false,
    hasError: false,
    serverSettings: undefined,
    siteSettings: undefined,
    siteAuthenticationSettings: undefined,
    siteSettingsForServerAdmin: undefined
  };

  public constructor(props: GeneralSiteSettingsSubspaceProps) {
    super(props);
    this.state = this.initialState;
  }

  public componentDidMount() {
    this.getData();
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidUpdate(prevProps: GeneralSiteSettingsSubspaceProps) {
    if (!_.isUndefined(this.props.siteName) && prevProps.siteName !== this.props.siteName) {
      this.updateState(this.initialState);
      this.getData();
      return;
    }

    if (!_.isUndefined(this.state.serverSettings)
      && !_.isUndefined(this.state.siteSettings)
      && !_.isUndefined(this.state.siteAuthenticationSettings)
      && !_.isUndefined(this.state.siteSettingsForServerAdmin)) {
        this.updateState({hasDataToRender: true});
    }
  }

  public render() {
    if (!this.state.hasDataToRender) {
      return null;
    } else if (this.state.hasError) {
      return <RouterError errorType={'default'} urls={this.props.urls} />;
    }

    return (
      <div className={'tb-stylish-settings'}>
        <GeneralSiteSettingsCoordinator {...this.state}/>
      </div>
    );
  }

  private updateState<K extends keyof GeneralSiteSettingsSubspaceState>(newState: Pick<GeneralSiteSettingsSubspaceState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private getData() {
    this.getServerAndSiteSettingsForServerAdmin();
    this.getSiteSettings();
    this.getSiteAuthenticationSettings();
  }

  private getServerAndSiteSettingsForServerAdmin = async () => {
    if (this.props.isSessionServerAdmin) {
      try {
        const serverSettings = await this.props.serverSettingsService.fetch();
        this.updateState({serverSettings: serverSettings});

        await this.props.server.getSessionInfoPromise();
        let siteSettings = await Sites.instance.getCurrentSiteSettingsForServerAdmin();
        this.copyServerSettingsToSite(siteSettings, serverSettings);
        this.updateState({siteSettingsForServerAdmin: siteSettings});
    } catch (e) {
        this.updateState({hasError: true});
      }
    } else {
      this.updateState({serverSettings: null});
      this.updateState({siteSettingsForServerAdmin: null});
    }
  };

  private getSiteSettings = async () => {
    try {
      await this.props.server.getSessionInfoPromise();
      const siteSettings = await Sites.instance.getCurrentSiteSettingsForSiteAdmin();
      this.updateState({siteSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  };

  private getSiteAuthenticationSettings = async () => {
    try {
      const siteAuthSettings = await SiteAuthenticationSettingsService.getSiteAuthenticationSettings(/* refresh */ true);
      this.updateState({siteAuthenticationSettings: siteAuthSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  };

  private copyServerSettingsToSite(siteSettings: ISiteSettingsForServerAdmin, serverSettings: IEditableServerSettings)
  {
    // copy server-wide settings that are necessary for site-specific sections
    siteSettings.settings.guestEnabledOnServer = serverSettings.guestEnabled;
  }
}
